/**
 * @module useLogger
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-logger
 *
 * @remarks
 * Logging composable with adapter pattern supporting console, consola, and pino.
 *
 * Key features:
 * - Multiple log levels (trace, debug, info, warn, error, fatal)
 * - Adapter pattern for console/consola/pino integration
 * - Enable/disable logging
 * - Fallback logger for undefined loggers
 * - Context logging support
 *
 * Uses adapter pattern to abstract logging implementation.
 *
 * @example
 * ```ts
 * import { useLogger } from '@vuetify/v0'
 *
 * const logger = useLogger()
 * logger.info('hello')
 * logger.warn('careful')
 * ```
 */

// Composables
import { createPluginContext } from '#v0/composables/createPlugin'

// Adapters
import { V0LoggerAdapter } from '#v0/composables/useLogger/adapters'

// Globals
import { __LOGGER_ENABLED__, IN_BROWSER } from '#v0/constants/globals'

// Utilities
import { isUndefined } from '#v0/utilities'

// Types
import type { LoggerAdapter } from '#v0/composables/useLogger/adapters'
import type { LogLevel } from '#v0/composables/useLogger/types'

// Exports
export {
  ConsolaLoggerAdapter,
  PinoLoggerAdapter,
  V0LoggerAdapter,
} from '#v0/composables/useLogger/adapters'

export { LoggerAdapter } from '#v0/composables/useLogger/adapters'

export type { LogLevel } from '#v0/composables/useLogger/types'

export interface LoggerContext {
  debug: (message: string, ...args: unknown[]) => void
  info: (message: string, ...args: unknown[]) => void
  warn: (message: string, ...args: unknown[]) => void
  error: (message: string, ...args: unknown[]) => void
  trace: (message: string, ...args: unknown[]) => void
  fatal: (message: string, ...args: unknown[]) => void
  level: (level: LogLevel) => void
  current: () => LogLevel
  enabled: () => boolean
  enable: () => void
  disable: () => void
  /** Active logger adapter. */
  adapter: LoggerAdapter
}

export interface LoggerOptions {
  adapter?: LoggerAdapter
  level?: LogLevel
  prefix?: string
  enabled?: boolean
}

export interface LoggerContextOptions extends LoggerOptions {
  namespace?: string
}

export interface LoggerPluginOptions extends LoggerContextOptions {}

/**
 * Creates a new logger instance.
 *
 * @param options The options for the logger instance.
 * @returns A new logger instance.
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-logger
 *
 * @example
 * ```ts
 * import { createLogger } from '@vuetify/v0'
 *
 * const logger = createLogger({
 *   level: 'debug',
 *   prefix: '[MyApp]',
 * })
 *
 * logger.info('This is an info message')
 * logger.debug('This is a debug message')
 * logger.error('This is an error message')
 * logger.level('debug')
 * logger.debug('This debug message will now be logged')
 * ```
 */
export function createLogger (options: LoggerOptions = {}): LoggerContext {
  const {
    adapter = new V0LoggerAdapter({ prefix: options.prefix }),
    level: initialLevel = 'info',
    enabled: initialEnabled = __LOGGER_ENABLED__,
  } = options

  let currentLevel = initialLevel
  let isEnabled = initialEnabled

  function value (level: LogLevel): number {
    const levels: Record<LogLevel, number> = {
      trace: 0,
      debug: 1,
      info: 2,
      warn: 3,
      error: 4,
      fatal: 5,
      silent: 6,
    }
    return levels[level] ?? 2
  }

  function can (level: LogLevel): boolean {
    if (!isEnabled) return false
    return value(level) >= value(currentLevel)
  }

  function format (message: string): string {
    return message
  }

  function debug (message: string, ...args: unknown[]) {
    if (can('debug')) adapter.debug(format(message), ...args)
  }

  function info (message: string, ...args: unknown[]) {
    if (can('info')) adapter.info(format(message), ...args)
  }

  function warn (message: string, ...args: unknown[]) {
    if (can('warn')) adapter.warn(format(message), ...args)
  }

  function error (message: string, ...args: unknown[]) {
    if (can('error')) adapter.error(format(message), ...args)
  }

  function trace (message: string, ...args: unknown[]) {
    if (can('trace')) adapter.trace?.(format(message), ...args)
  }

  function fatal (message: string, ...args: unknown[]) {
    if (can('fatal')) adapter.fatal?.(format(message), ...args)
  }

  function level (newLevel: LogLevel) {
    currentLevel = newLevel
  }

  function current () {
    return currentLevel
  }

  function enabled () {
    return isEnabled
  }

  function enable () {
    isEnabled = true
  }

  function disable () {
    isEnabled = false
  }

  return {
    debug,
    info,
    warn,
    error,
    trace,
    fatal,
    level,
    current,
    enabled,
    enable,
    disable,
    adapter,
  } as LoggerContext
}

const NAMESPACE = 'v0:logger'

// PHILOSOPHY §9.2 Layer-0 exception: this IS the logger bootstrap —
// useLogger() here would be infinite recursion.
function createFallbackLogger (namespace = NAMESPACE): LoggerContext {
  function format (message: string, type: string): string {
    return `[${namespace} ${type}] ${message}`
  }

  return {
    debug: (message: string, ...args: unknown[]) => console.debug(format(message, 'debug'), ...args),
    info: (message: string, ...args: unknown[]) => console.info(format(message, 'info'), ...args),
    warn: (message: string, ...args: unknown[]) => console.warn(format(message, 'warn'), ...args),
    error: (message: string, ...args: unknown[]) => console.error(format(message, 'error'), ...args),
    trace: (message: string, ...args: unknown[]) => console.trace(format(message, 'trace'), ...args),
    fatal: (message: string, ...args: unknown[]) => console.error(format(message, 'fatal'), ...args),
    level: () => {},
    current: () => 'info' as LogLevel,
    enabled: () => true,
    enable: () => {},
    disable: () => {},
    adapter: new V0LoggerAdapter({ prefix: namespace }),
  } as LoggerContext
}

/**
 * Creates a new logger context.
 *
 * @param options The options for the logger context.
 * @template E The type of the logger context.
 * @returns A new logger context.
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-logger
 *
 * @example
 * ```ts
 * import { createLoggerContext } from '@vuetify/v0'
 *
 * export const [useAppLogger, provideAppLogger, appLogger] = createLoggerContext({
 *   namespace: 'app:logger',
 *   level: 'debug',
 * })
 * ```
 */
const [createLoggerContext, createLoggerPlugin, useLoggerContext] =
  /* @__PURE__ */ createPluginContext<LoggerContextOptions, LoggerContext>(
    NAMESPACE,
    options => createLogger(options),
    {
      fallback: ns => createFallbackLogger(ns),
      inspect: ctx => ({
        level: ctx.current(),
        enabled: ctx.enabled(),
        adapter: ctx.adapter.constructor.name,
      }),
      setup: (context, _app, _options) => {
        if (typeof __DEV__ !== 'undefined' && __DEV__ && IN_BROWSER) {
          ;(window as unknown as Record<string, unknown>).__v0Logger__ = context
        }
      },
    },
  )

export { createLoggerContext, createLoggerPlugin }

// Wraps a resolved logger so every message carries a `[scope]` segment after the
// plugin prefix. Level gating, enable/disable, and the adapter are delegated to the
// shared instance via spread, so a scoped logger never diverges from its parent.
function createScopedLogger (logger: LoggerContext, scope: string): LoggerContext {
  const tag = `[${scope}]`

  function wrap (method: (message: string, ...args: unknown[]) => void) {
    return (message: string, ...args: unknown[]) => method(`${tag} ${message}`, ...args)
  }

  return {
    ...logger,
    debug: wrap(logger.debug),
    info: wrap(logger.info),
    warn: wrap(logger.warn),
    error: wrap(logger.error),
    trace: wrap(logger.trace),
    fatal: wrap(logger.fatal),
  }
}

/**
 * Consumes the application logger provided by `createLoggerPlugin`.
 *
 * Call with no argument to use the configured logger as-is. Pass a scope key to get a
 * logger that prefixes every message with a `[scope]` segment after the plugin prefix —
 * useful for telling apart output from composables that share the app-level logger. A
 * scoped logger shares the underlying logger's level, enabled state, and adapter; only
 * the message prefix differs. A blank scope is treated as no scope.
 *
 * @param scope Optional per-caller scope key prefixed onto every message.
 * @returns The application logger, or a scoped wrapper around it.
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-logger
 *
 * @example
 * ```ts
 * import { useLogger } from '@vuetify/v0'
 *
 * const logger = useLogger('createKanban')
 *
 * logger.warn('drop rejected')
 * // → [v0 warn] [createKanban] drop rejected
 * ```
 */
export function useLogger (scope?: string): LoggerContext {
  const logger = useLoggerContext()

  return isUndefined(scope) || !scope.trim() ? logger : createScopedLogger(logger, scope)
}
